/**
 * CartPage
 * Full-page cart view (alternative to drawer)
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  IconButton,
  Stack,
  Chip,
  Divider,
  AppBar,
  Toolbar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { useCartStore, useCartItemsWithDetails } from '../store/cart';
import { formatPrice } from '../lib/catalog';
import { CartBadge } from '../components/commerce';
import { trackPageView } from '../lib/analytics';

const CartPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const items = useCartItemsWithDetails();
  const { setQty, remove, clear, getTotal, hasSubscription, hasMixedTypes } = useCartStore();

  const total = getTotal();
  const hasItems = items.length > 0;

  // Track page view on mount
  React.useEffect(() => {
    trackPageView('cart');
  }, []);

  const oneTimeTotal = items
    .filter(i => i.sku.type !== 'subscription')
    .reduce((sum, i) => sum + i.subtotal, 0);

  const recurringTotal = items
    .filter(i => i.sku.type === 'subscription')
    .reduce((sum, i) => sum + i.subtotal, 0);

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'subscription':
        return 'Monthly';
      case 'metered':
        return 'Per Hour';
      default:
        return 'One-time';
    }
  };

  const getTypeColor = (type: string): 'primary' | 'secondary' | 'info' => {
    switch (type) {
      case 'subscription':
        return 'secondary';
      case 'metered':
        return 'info';
      default:
        return 'primary';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 'bold',
            }}
          >
            REDEX
          </Typography>

          <Button
            component={Link}
            to="/pricing"
            startIcon={<BackIcon />}
            sx={{ mr: 2 }}
          >
            Back to Pricing
          </Button>

          <CartBadge onClick={() => {}} />
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <CartIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            Your Cart
          </Typography>
          {hasItems && (
            <Chip label={`${items.length} item(s)`} color="primary" />
          )}
        </Box>

        {!hasItems ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <CartIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Your cart is empty
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Add products from our pricing page to get started
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/pricing')}
            >
              View Pricing
            </Button>
          </Paper>
        ) : (
          <Stack spacing={4}>
            {/* Cart Items */}
            <Paper>
              <List disablePadding>
                {items.map((item, index) => (
                  <React.Fragment key={item.skuId}>
                    <ListItem sx={{ py: 3, px: 3 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="h6">{item.sku.name}</Typography>
                          <Chip
                            label={getTypeLabel(item.sku.type)}
                            size="small"
                            color={getTypeColor(item.sku.type)}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {item.sku.description}
                        </Typography>

                        {/* Quantity controls for metered items */}
                        {item.sku.type === 'metered' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                            <IconButton
                              size="small"
                              onClick={() => setQty(item.skuId, item.qty - 1)}
                              disabled={item.qty <= 1}
                            >
                              <RemoveIcon />
                            </IconButton>
                            <Typography sx={{ minWidth: 60, textAlign: 'center' }}>
                              {item.qty} hour{item.qty > 1 ? 's' : ''}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => setQty(item.skuId, item.qty + 1)}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ textAlign: 'right', ml: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                          {formatPrice(item.subtotal)}
                          {item.sku.type === 'subscription' && (
                            <Typography component="span" variant="body2" color="text.secondary">
                              /mo
                            </Typography>
                          )}
                        </Typography>
                        <IconButton
                          onClick={() => remove(item.skuId)}
                          color="error"
                          sx={{ mt: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItem>
                    {index < items.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>

            {/* Mixed cart warning */}
            {hasMixedTypes() && (
              <Paper
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  border: `1px solid ${theme.palette.warning.main}`,
                }}
              >
                <Typography variant="body2">
                  <strong>Note:</strong> Your cart contains both one-time and subscription items.
                  You'll complete two checkout steps to process both payment types.
                </Typography>
              </Paper>
            )}

            {/* Order Summary */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>

              <Stack spacing={1} sx={{ mb: 3 }}>
                {oneTimeTotal > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">One-time charges:</Typography>
                    <Typography>{formatPrice(oneTimeTotal)}</Typography>
                  </Box>
                )}

                {hasSubscription() && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Monthly recurring:</Typography>
                    <Typography>{formatPrice(recurringTotal)}/mo</Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total Due Today:</Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {formatPrice(total)}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => navigate('/checkout')}
                >
                  Proceed to Checkout
                </Button>
                <Button
                  variant="outlined"
                  onClick={clear}
                >
                  Clear Cart
                </Button>
              </Stack>
            </Paper>
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default CartPage;
